import z from 'zod';

export const billingCycles = ['one_month', 'three_months', 'six_months', 'twelve_months', 'custom'] as const;

const paymentMethodSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pix')
  }),
  z.object({
    type: z.literal('credit_card'),
    cardInfo: z.string()
  }),
  z.object({
    type: z.literal('bankslip')
  })
]);

const baseOptionalFields = {
  price: z
    .number()
    .min(5, { error: 'O valor mínimo é de R$5,00.' })
    .max(10000, { error: 'O valor máximo é de R$10.000,00.' })
    .optional(),
  billingCycle: z.enum([...billingCycles]).optional(),
  methods: paymentMethodSchema.array().min(1, { error: 'Selecione ao menos um método de pagamento.' }).default([]),
  billingConfig: z
    .object({
      value: z.number().min(1, { error: 'O valor mínimo é 1.' }).optional(),
      type: z.enum(['months', 'years']).optional()
    })
    .optional(),
  recurrenceCount: z
    .number()
    .max(99, { error: 'O valor máximo de cobranças é 99.' })
    .refine(v => v === 0 || v >= 2, {
      message: 'O valor deve no mínimo 2.'
    })
    .optional(),
  maximumLatePayments: z
    .number()
    .min(1, { error: 'O valor mínimo de atraso é 1.' })
    .max(999, { error: 'O valor máximo de atraso é 999.' })
    .optional(),
  activationFeeValue: z.number().min(5, { error: 'O valor mínimo é de R$5,00.' }).optional(),
  trialDays: z.number().min(1, { error: 'O valor mínimo de dias de trial é 1.' }).optional(),
  trialEnabled: z.boolean().optional(),
  activationFeeEnabled: z.boolean().optional()
};

const open = z
  .object({
    type: z.literal('open'),
    ...baseOptionalFields,
    methods: baseOptionalFields.methods.unwrap()
  })
  .extend({ price: z.number().optional() })
  .catchall(z.any());

const free = z
  .object({
    type: z.literal('free'),
    ...baseOptionalFields
  })
  .extend({ price: z.number().optional(), methods: z.array(paymentMethodSchema).optional() });

const oneTime = z
  .object({
    type: z.literal('oneTime'),
    ...baseOptionalFields,
    price: baseOptionalFields.price.unwrap(),
    methods: baseOptionalFields.methods.unwrap()
  })
  .strip();

const subscription = z
  .object({
    type: z.literal('subscription'),
    ...baseOptionalFields,
    methods: baseOptionalFields.methods.unwrap(),
    price: baseOptionalFields.price.unwrap(),
    billingCycle: baseOptionalFields.billingCycle.unwrap(),
    recurrenceCount: baseOptionalFields.recurrenceCount.unwrap(),
    maximumLatePayments: baseOptionalFields.maximumLatePayments.unwrap(),
    activationFeeValue: baseOptionalFields.activationFeeValue.optional(),
    trialDays: baseOptionalFields.trialDays.optional(),
    billingConfig: baseOptionalFields.billingConfig.unwrap()
  })
  .strip();

export const paymentSchema = z.discriminatedUnion('type', [open, free, oneTime, subscription]);

export type PriceFormValues = z.infer<typeof paymentSchema>;

export const recoveryEmailSenderName = z
  .string()
  .trim()
  .min(3, 'O nome deve ter pelo menos 3 caracteres.')
  .max(50, 'O nome deve ter no máximo 50 caracteres.');

const upgradeSalesItemSchema = z.discriminatedUnion('feature', [
  z.object({
    feature: z.literal('sales_recovery'),
    recoveryEmailSenderName
  }),

  z.object({
    feature: z.enum([
      'multiple_cards',
      'combined_payment',
      'installments_without_limitation',
      'installments_without_interest'
    ])
  })
]);

export const productValidator = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  email: z.email(),
  title: z
    .string()
    .min(3, 'O título deve ter pelo menos 3 caracteres.')
    .max(120, 'O título deve ter no máximo 120 caracteres.'),
  description: z
    .string()
    .min(100, 'A descrição deve ter pelo menos 100 caracteres.')
    .max(1000, 'A descrição deve ter no máximo 1000 caracteres.'),
  image: z.string().optional(),
  payment: paymentSchema,
  categories: z.array(z.string()).min(1, 'Selecione ao menos uma categoria.').optional(),
  upgradeSales: z.array(upgradeSalesItemSchema),
  deliverables: z.array(
    z.object({
      hash: z.string(),
      filename: z.string(),
      id: z.string(),
      deliverableId: z.string(),
      size: z.number().optional()
    })
  )
});

export type productValidatorType = z.infer<typeof productValidator>;
